import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, ClipboardList, Phone, Send } from "lucide-react";
import { toast } from "sonner";
import Seo from "../components/Seo";
import { PhotoUpload } from "../components/PhotoUpload";
import { API, BUSINESS } from "../lib/site";
import { CONDITIONS, FORM_CATEGORIES } from "../data/content";

const inputCls =
  "w-full border border-white/15 bg-white/5 px-4 py-3 text-base text-white placeholder:text-white/40 transition-colors duration-150 focus:border-ves-green focus:outline-none focus:ring-2 focus:ring-ves-green/40";
const labelCls = "mb-1.5 block text-sm font-semibold text-white/80";

const EMPTY = {
  company: "", contact_name: "", phone: "", email: "", location: "",
  category: "", brand: "", model_number: "", quantity: "", condition: "",
  description: "", website: "",
};

export default function SellSurplus() {
  const [params] = useSearchParams();
  const [form, setForm] = useState(EMPTY);
  const [photos, setPhotos] = useState([]);
  const [status, setStatus] = useState("idle");

  useEffect(() => {
    const cat = params.get("category");
    if (cat) setForm((f) => ({ ...f, category: cat }));
  }, [params]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      photos.forEach((p) => fd.append("photos", p));
      await axios.post(`${API}/inquiries`, fd);
      setStatus("success");
    } catch (err) {
      setStatus("idle");
      toast.error(err.response?.data?.detail || "Submission failed — please call us instead.");
    }
  };

  return (
    <div data-testid="sell-surplus-page">
      <Seo
        title="Sell Your Surplus | Request a Purchasing Offer — Velocity Electrical Surplus"
        description="Submit your electrical surplus inventory or photos for an accurate purchasing offer. Circuit breakers, wire, switchgear, transformers and complete inventories. Cash paid nationwide."
      />

      <section className="border-b border-white/10 bg-ves-charcoal/40">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
          <p className="font-heading text-xs font-bold tracking-[0.25em] text-ves-green">
            FREE SURPLUS EQUIPMENT EVALUATIONS
          </p>
          <h1 className="mt-3 font-heading text-4xl font-black text-white sm:text-5xl">
            SELL YOUR SURPLUS
          </h1>
          <p className="mt-4 max-w-3xl text-base leading-relaxed text-white/75 sm:text-lg">
            Submit your inventory list or photos for an accurate purchasing offer. The more
            detail you provide — manufacturers, model numbers, quantities and condition — the
            faster and sharper our offer. Same-day offers when possible.
          </p>
        </div>
      </section>

      <section className="bg-ves-black py-16 sm:py-20">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 lg:grid-cols-3 lg:px-8">
          {/* Form */}
          <div className="border border-white/10 bg-ves-charcoal/50 p-6 sm:p-8 lg:col-span-2">
            {status === "success" ? (
              <div className="border border-ves-green/50 bg-ves-forest/40 p-8 text-center" data-testid="sell-success">
                <CheckCircle2 className="mx-auto h-12 w-12 text-ves-green" />
                <h2 className="mt-4 font-heading text-2xl font-black text-white">
                  YOUR OFFER REQUEST HAS BEEN SENT.
                </h2>
                <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-white/75">
                  Our purchasing team will review your submission and respond with an offer.
                  For an immediate answer, call {BUSINESS.manager} directly at{" "}
                  <a href={BUSINESS.phoneTel} className="font-bold text-ves-green">{BUSINESS.phoneDisplay}</a>.
                </p>
                <button
                  onClick={() => { setForm(EMPTY); setPhotos([]); setStatus("idle"); }}
                  className="mt-6 border border-white/25 px-6 py-3 font-heading text-sm font-black tracking-wide text-white transition-colors duration-150 hover:border-ves-green hover:text-ves-green"
                  data-testid="sell-another-button"
                >
                  SUBMIT ANOTHER ITEM
                </button>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="grid gap-5 sm:grid-cols-2" data-testid="sell-form">
                <div>
                  <label htmlFor="s-company" className={labelCls}>Company Name</label>
                  <input id="s-company" value={form.company} onChange={set("company")} className={inputCls} placeholder="Company (optional)" data-testid="sell-company-input" />
                </div>
                <div>
                  <label htmlFor="s-contact" className={labelCls}>Contact Name *</label>
                  <input id="s-contact" required value={form.contact_name} onChange={set("contact_name")} className={inputCls} placeholder="Your name" data-testid="sell-contact-input" />
                </div>
                <div>
                  <label htmlFor="s-phone" className={labelCls}>Phone Number *</label>
                  <input id="s-phone" required type="tel" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="(555) 555-5555" data-testid="sell-phone-input" />
                </div>
                <div>
                  <label htmlFor="s-email" className={labelCls}>Email *</label>
                  <input id="s-email" required type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="you@email.com" data-testid="sell-email-input" />
                </div>
                <div>
                  <label htmlFor="s-location" className={labelCls}>City and State</label>
                  <input id="s-location" value={form.location} onChange={set("location")} className={inputCls} placeholder="e.g. Detroit, MI" data-testid="sell-location-input" />
                </div>
                <div>
                  <label htmlFor="s-category" className={labelCls}>Equipment Category</label>
                  <select id="s-category" value={form.category} onChange={set("category")} className={`${inputCls} appearance-none`} data-testid="sell-category-select">
                    <option value="" className="bg-ves-charcoal">Select a category…</option>
                    {FORM_CATEGORIES.map((c) => (
                      <option key={c} value={c} className="bg-ves-charcoal">{c}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="s-brand" className={labelCls}>Manufacturer / Brand</label>
                  <input id="s-brand" value={form.brand} onChange={set("brand")} className={inputCls} placeholder="e.g. Square D, Siemens, Eaton" data-testid="sell-brand-input" />
                </div>
                <div>
                  <label htmlFor="s-model" className={labelCls}>Model Number</label>
                  <input id="s-model" value={form.model_number} onChange={set("model_number")} className={inputCls} placeholder="From the nameplate or label" data-testid="sell-model-input" />
                </div>
                <div>
                  <label htmlFor="s-qty" className={labelCls}>Quantity</label>
                  <input id="s-qty" value={form.quantity} onChange={set("quantity")} className={inputCls} placeholder="e.g. 2 pallets, 40 units, 3 reels" data-testid="sell-quantity-input" />
                </div>
                <div>
                  <label htmlFor="s-condition" className={labelCls}>Condition</label>
                  <select id="s-condition" value={form.condition} onChange={set("condition")} className={`${inputCls} appearance-none`} data-testid="sell-condition-select">
                    <option value="" className="bg-ves-charcoal">Select condition…</option>
                    {CONDITIONS.map((c) => (
                      <option key={c} value={c} className="bg-ves-charcoal">{c}</option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="s-desc" className={labelCls}>Description</label>
                  <textarea id="s-desc" rows={5} value={form.description} onChange={set("description")} className={inputCls} placeholder="Tell us what you have — quantities, sizes, ages, how it was stored…" data-testid="sell-description-input" />
                </div>
                <div className="sm:col-span-2">
                  <span className={labelCls}>Photos</span>
                  <PhotoUpload files={photos} setFiles={setPhotos} idPrefix="sell-photo" />
                </div>
                <input type="text" name="website" value={form.website} onChange={set("website")} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
                <button
                  type="submit"
                  disabled={status === "sending"}
                  className="flex items-center justify-center gap-2 bg-ves-green px-6 py-4 font-heading text-base font-black tracking-wide text-ves-black transition-colors duration-150 hover:bg-white disabled:opacity-60 sm:col-span-2"
                  data-testid="sell-submit-button"
                >
                  <Send className="h-5 w-5" />
                  {status === "sending" ? "SENDING…" : "REQUEST A PURCHASING OFFER"}
                </button>
              </form>
            )}
          </div>

          {/* Side panel */}
          <aside className="space-y-6">
            <div className="border border-white/10 bg-ves-charcoal/50 p-6">
              <h2 className="flex items-center gap-2 font-heading text-lg font-black text-white">
                <ClipboardList className="h-5 w-5 text-ves-green" /> WHAT HAPPENS NEXT
              </h2>
              <ol className="mt-4 space-y-4">
                {[
                  "We review your photos and equipment details.",
                  "You receive a competitive purchasing offer — same-day when possible.",
                  "We arrange freight, loading and trucking for qualifying purchases.",
                  "Payment before equipment is loaded, when agreed.",
                ].map((s, i) => (
                  <li key={i} className="flex gap-3 text-sm text-white/75">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center bg-ves-green font-heading text-xs font-black text-ves-black">
                      {i + 1}
                    </span>
                    {s}
                  </li>
                ))}
              </ol>
            </div>
            <div className="border border-ves-green/40 bg-ves-forest/40 p-6">
              <h2 className="font-heading text-lg font-black text-white">NEED A FASTER OFFER?</h2>
              <p className="mt-2 text-sm text-white/75">
                Call or text photos directly to our purchasing manager.
              </p>
              <a
                href={BUSINESS.phoneTel}
                className="mt-4 flex items-center justify-center gap-2 bg-ves-green px-5 py-3.5 font-heading text-sm font-black tracking-wide text-ves-black transition-colors duration-150 hover:bg-white"
                data-testid="sell-side-call-btn"
              >
                <Phone className="h-4 w-4" /> {BUSINESS.manager} — {BUSINESS.phoneDisplay}
              </a>
            </div>
            <div className="border border-white/10 bg-ves-charcoal/50 p-6">
              <h2 className="font-heading text-sm font-black tracking-widest text-ves-green">
                PHOTO TIPS
              </h2>
              <ul className="mt-3 list-disc space-y-1.5 pl-5 text-sm text-white/70">
                <li>Photograph nameplates, labels and model numbers.</li>
                <li>Include overall shots of pallets, reels or rooms.</li>
                <li>Show any damage honestly — we buy as-is.</li>
              </ul>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
